import { useInView } from "react-intersection-observer";
import type { OfferTemplate } from "@b2bportal/media-ads-api";
import { claimedOfferDialogContent } from "../HtsMediaClaimDialog";
import { ClaimedOfferTile } from "./ClaimedOfferTile";
import { getOfferTileTrackingProperties } from "./offerTileTracking";
import { getOfferImageSrc } from "@/modules/hotel-funded-offers/offerImage";
import {
  type OfferScreen,
  IsOfferClaimed,
  OfferRawEventName,
  useEmitRbcRawEventOnce,
} from "@/tracking/offers";

export interface HtsMediaClaimedDealTileProps {
  offer: OfferTemplate;
  /** Position of this tile in its section (0-based). */
  tileIndex: number;
  screen: OfferScreen;
  className?: string;
}

/**
 * Sponsored offer the user already loaded (`fetchAds.claimedOffers`).
 * Amplitude only: there is no ad to attribute HTS Media events to.
 */
export const HtsMediaClaimedDealTile = ({
  offer,
  tileIndex,
  screen,
  className,
}: HtsMediaClaimedDealTileProps) => {
  const { assets } = offer;
  const trackingProperties = getOfferTileTrackingProperties({
    offer,
    tileIndex,
  });

  const { ref, inView } = useInView({
    threshold: [0.5],
    delay: 1000,
    triggerOnce: true,
  });
  useEmitRbcRawEventOnce(
    {
      name: OfferRawEventName.ViewedOfferTile,
      properties: {
        ...trackingProperties,
        screen,
        is_claimable: true,
        is_offer_claimed: IsOfferClaimed.true,
      },
    },
    inView
  );

  return (
    <ClaimedOfferTile
      rootRef={ref}
      className={className}
      content={claimedOfferDialogContent(offer)}
      imageSrc={getOfferImageSrc(assets.imageUrl ?? assets.wideImageUrl)}
      description={assets.bodyText ?? assets.longOfferText ?? ""}
      trackingProperties={trackingProperties}
      screen={screen}
    />
  );
};
